import type { I18n } from "@lingui/core"
import { msg } from "@lingui/core/macro"
import { formatDateTime } from "./format-date"

/**
 * Pre-fix the create flow used to stamp this exact pattern as the actual
 * subject — "Untitled meeting · 4 May, 04:01 PM" or any localised equivalent —
 * defeating the topic fallback. New meetings now leave subject empty (see
 * NewMeetingDialogView), but every existing row in the DB still carries the
 * stamp. This pattern catches both the EN string + the localised NL form
 * ("Naamloze sessie · …") so legacy rows render their top topic too.
 *
 * Match is intentionally loose: any subject whose trim begins with the
 * localised "Untitled meeting" / "Naamloze" prefix and contains a `·` is
 * treated as auto-stamped. False positives (a user who literally types
 * "Untitled meeting · the contract") are not worth optimising for.
 */
const STAMPED_SUBJECT_PREFIXES = ["untitled meeting", "naamloze"] as const

/** Teams discovery used to persist this exact placeholder as the subject. */
const STORED_NO_SUBJECT = "(no subject)"

function looksAutoStamped(subject: string): boolean {
  if (!subject.includes("·")) return false
  const lower = subject.toLowerCase().trim()
  return STAMPED_SUBJECT_PREFIXES.some((prefix) => lower.startsWith(prefix))
}

function isStoredNoSubject(subject: string): boolean {
  return subject.toLowerCase() === STORED_NO_SUBJECT
}

/**
 * Pick the human-friendly title for a meeting card / hero / detail header.
 *
 * Why: meetings without a subject used to render as "Untitled meeting · May 4,
 * 04:01 PM" which is information-free. When the meeting has been processed and
 * has a top topic, it's a better signal of "what was this about" than a stamp.
 *
 * Order: real (non-auto-stamped) trimmed subject → first topic → Lite dated
 * fallback when the caller says this is a Lite meeting → stamped subject →
 * "Untitled meeting". A stored "(no subject)" counts as blank. Callers that
 * cannot tell Lite from Full omit `lite` and keep the Full wording.
 *
 * Lite writes a short title after the recording when the subject is still
 * blank. Until that title lands, and if the call fails, a blank (or legacy
 * auto-stamped) Lite subject with no topic reads "Recording · 27 Oct at
 * 7:22 PM". The date comes from `formatDateTime`.
 */
export interface IDisplayMeetingTitleOptions {
  /** Blank Lite subjects use the dated "Recording" fallback until a title is written. */
  lite?: boolean
  /** Start time for that fallback. Ignored unless `lite` is set. */
  startedAt?: Date | string | number | null
}

export function displayMeetingTitle(
  subject: string | null | undefined,
  topics: string[] | null | undefined,
  i18n: I18n,
  options?: IDisplayMeetingTitleOptions,
): string {
  const trimmed = subject?.trim()
  const trimmedSubject = trimmed && !isStoredNoSubject(trimmed) ? trimmed : ""
  if (trimmedSubject && !looksAutoStamped(trimmedSubject)) return trimmedSubject
  const topTopic = topics?.[0]?.trim()
  if (topTopic) return topTopic
  if (options?.lite) {
    const dateLabel = formatDateTime(i18n, options.startedAt, { year: false })
    return dateLabel ? i18n._(msg`Recording · ${dateLabel}`) : i18n._(msg`Recording`)
  }
  if (trimmedSubject) return trimmedSubject
  return i18n._(msg`Untitled meeting`)
}
